import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@caruso/ui';
import { Button, Input } from '@caruso/ui';
import { Copy, Send, ExternalLink } from 'lucide-react';
import { clientsService } from '@/services/clients.service';
import { useQuickSaleLink } from '@/hooks/use-quick-sale-link';

interface QuickSaleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  productName: string;
}

export function QuickSaleModal({
  open,
  onOpenChange,
  productId,
  productName,
}: QuickSaleModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const { generateQuickSaleLink, copyQuickSaleLink, shareQuickSaleLink } = useQuickSaleLink();

  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['clients', searchTerm],
    queryFn: () =>
      clientsService.getAll({
        page: 1,
        pageSize: 10,
        search: searchTerm,
        archived: false,
      }),
    enabled: searchTerm.length >= 2,
  });

  const selectedClient = clientsData?.data.find(
    (client) => client.id === selectedClientId
  );

  const link = selectedClientId
    ? generateQuickSaleLink(selectedClientId, productId)
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] bg-brand-ink border-brand-gold/20">
        <DialogHeader>
          <DialogTitle className="text-brand-gold font-serif">Gerar Link de Venda Rápida</DialogTitle>
          <DialogDescription className="text-brand-ivory/60">
            Crie um link personalizado para vender <strong className="text-brand-gold">{productName}</strong> diretamente
            para um cliente específico
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Busca de cliente */}
          <div>
            <label className="text-sm font-medium text-brand-ivory/60 mb-1 block">
              Buscar Cliente
            </label>
            <Input
              placeholder="Digite o nome, email ou telefone do cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>

          {/* Lista de clientes */}
          {isLoading && (
            <div className="text-center py-4 text-brand-ivory/60">Buscando clientes...</div>
          )}

          {searchTerm.length >= 2 && !isLoading && clientsData && (
            <div className="max-h-[200px] overflow-y-auto border border-brand-gold/20 rounded-md bg-brand-smoke/30">
              {clientsData.data.length === 0 ? (
                <div className="text-center py-4 text-brand-ivory/60">
                  Nenhum cliente encontrado
                </div>
              ) : (
                <div className="divide-y divide-brand-gold/10">
                  {clientsData.data.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => setSelectedClientId(client.id)}
                      className={`w-full px-4 py-3 text-left hover:bg-brand-gold/10 transition-colors ${
                        selectedClientId === client.id ? 'bg-brand-gold/20' : ''
                      }`}
                    >
                      <div className="font-medium text-brand-ivory">{client.name}</div>
                      <div className="text-sm text-brand-ivory/60">
                        {client.email || client.phone || 'Sem contato'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Link gerado */}
          {selectedClient && (
            <div className="border-t border-brand-gold/20 pt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-brand-ivory/60 mb-1 block">
                  Cliente Selecionado
                </label>
                <div className="px-3 py-2 bg-brand-smoke/50 rounded-md border border-brand-gold/20">
                  <div className="font-medium text-brand-gold">{selectedClient.name}</div>
                  <div className="text-sm text-brand-ivory/60">
                    {selectedClient.email || selectedClient.phone}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-brand-ivory/60 mb-1 block">
                  Link de Venda Rápida
                </label>
                <div className="flex gap-2">
                  <Input
                    value={link}
                    readOnly
                    className="flex-1 font-mono text-sm bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                  <Button
                    onClick={() => copyQuickSaleLink(selectedClientId, productId)}
                    variant="outline"
                    size="icon"
                    title="Copiar link"
                    className="border-brand-gold/20 hover:bg-brand-gold/10"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => window.open(link, '_blank')}
                    variant="outline"
                    size="icon"
                    title="Abrir link"
                    className="border-brand-gold/20 hover:bg-brand-gold/10"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() =>
                    shareQuickSaleLink(
                      selectedClientId,
                      productId,
                      selectedClient.name,
                      productName
                    )
                  }
                  className="flex-1"
                  variant="outline"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Enviar via WhatsApp
                </Button>
                <Button
                  onClick={() => copyQuickSaleLink(selectedClientId, productId)}
                  className="flex-1"
                  variant="premium"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar Link
                </Button>
              </div>
            </div>
          )}

          {searchTerm.length > 0 && searchTerm.length < 2 && (
            <div className="text-center py-4 text-brand-ivory/60 text-sm">
              Digite pelo menos 2 caracteres para buscar
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
